import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { debounceTime } from "rxjs/operators";
import {
  Component,
  OnDestroy,
  ViewChild,
  EventEmitter,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import {
  ProfileUpdateRequestImpl,
  CurrencyPairImpl,
  UnitType,
} from "shared/cubiq-model-multi-asset";
import { BfxUserSettingsComponentState } from "./bfx-user-settings.service";
import { BfxUserSettingsValidityState } from "./bfx-user-settings-page.component";
import {
  ProfilePromiseActionService,
  CcyPairPromiseLookupService,
  ActiveUser,
  ProfileSettingKeys,
} from "shared/cubiq-services";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { CCY_PAIR_SORT } from "./model/bfx-settings-constants";
import { IBfxUserSettingsPageSubComponent } from "app/bfx-user-settings/bfx-user-settings-page.component";
import { BfxUserSettingsComponent } from "app/bfx-user-settings/bfx-user-settings.component";
import { BfxOrderBlotterSettingsComponent } from "app/bfx-user-settings/bfx-order-blotter-settings.component";
import { BigNumber } from "shared/cubiq-model-multi-asset";
import { DataTable } from "@bfx/primeng/datatable";
import { LazyLoadEvent } from "@bfx/primeng/api";

export class BfxCcypairTempSetting {
  public ccyPairIdentifier: string = "";
  public warmSetting: BigNumber = null;
  public hotSetting: BigNumber = null;
  public temperatureTypeId: number = 0;
  public typeSetting: any = {};
  public precision: number = 0;
  public minValue: number = 0;
  public maxValue: number = 99999;
  public previousValues: any = {};
  public isDefault: boolean = true;
  public defaultWarm: BigNumber = null;
  public defaultHot: BigNumber = null;
  public defaultTypeId: number = 0;
  public isEdited: boolean = false;
  public prevType: any = {};

  constructor(
    ccyPairIdentifier: string,
    warmSetting: BigNumber,
    hotSetting: BigNumber,
    temperatureTypeId: number,
    isDefault: boolean,
    defaultWarm: BigNumber,
    defaultHot: BigNumber,
    defaultTypeId,
  ) {
    this.ccyPairIdentifier = ccyPairIdentifier;
    this.warmSetting = warmSetting;
    this.hotSetting = hotSetting;
    this.temperatureTypeId = temperatureTypeId;
    this.isDefault = isDefault;
    this.defaultWarm = defaultWarm;
    this.defaultHot = defaultHot;
    this.defaultTypeId = defaultTypeId;
  }

  public remember(): void {
    this.previousValues = Object.assign(this.previousValues, this);
  }

  public reset(): BfxCcypairTempSetting {
    Object.assign(this, this.previousValues);

    return this;
  }
}

@Component({
  selector: "bfx-order-blotter-settings-temperature",
  templateUrl: "./html/bfx-order-blotter-settings-temperature.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BfxOrderBlotterSettingsTemperatureComponent
  implements IBfxUserSettingsPageSubComponent<BfxOrderBlotterSettingsComponent>, OnDestroy
{
  readonly PAIR_TEMPERATURE_ROW_COUNT = 12;
  public categoryComponent: BfxOrderBlotterSettingsComponent;
  public parentComponent: BfxUserSettingsComponent;
  private readonly pipsPrecision: number = 0;
  private readonly percentagePrecision: number = 2;
  private readonly pipsMinVal: number = 0;
  private readonly percentageMinVal: number = 0.0;
  private readonly pipsMaxVal: number = 99999;
  private readonly percentageMaxVal: number = 999.99;

  public ccyPairsObjList: Array<BfxCcypairTempSetting> = [];
  public ccyPairObjRecords: Array<BfxCcypairTempSetting> = [];

  public ready: boolean = false;
  public loading: boolean = false;
  public panelHeight: number = 0;

  // Must continue to match CubiqTS.UnitType enum order
  private temperatureTypes: Map<CubiqTS.UnitType, { value: string; label: string }> = new Map([
    [CubiqTS.UnitType.PIPS, { label: "Pips", value: "Pips" }],
    [CubiqTS.UnitType.PERCENT, { label: "%", value: "%" }],
  ]);
  public typeSuggestions: Array<any> = [...this.temperatureTypes.values()];
  private tempValueToUpdateActiveUser: string;
  public showSearch: boolean = false;
  private ccyPairsList: Array<CurrencyPairImpl> = [];
  private search: EventEmitter<string | void>;
  private columnToFilter: any = null;
  public filterValue: string = "";
  public first: number = 0;
  public totalRecords: number = 0;
  private failValidation = false;

  @ViewChild("dataTable", { static: true })
  public dataTable: DataTable;

  constructor(
    public profileService: ProfilePromiseActionService,
    public translationService: TranslateService,
    public ccyPairService: CcyPairPromiseLookupService,
    public cd: ChangeDetectorRef,
  ) {
    this.ccyPairsList = this.ccyPairService.getBase().getAll();
    this.subscribeToSearch();
  }

  public onTabActivated(event: any): void {}

  public initialize(): void {
    let savedSettingsValues: Array<any> = [];
    let activeUser: ActiveUser = this.parentComponent.activeUser;

    this.loading = true;

    if (
      activeUser.profileSettings &&
      activeUser.profileSettings.has(ProfileSettingKeys.userProfileSettingsOrderBlotterTemperatures)
    ) {
      let savedSettings: string = activeUser.profileSettings.get(
        ProfileSettingKeys.userProfileSettingsOrderBlotterTemperatures,
      );

      if (savedSettings != undefined) {
        try {
          savedSettingsValues = JSON.parse(savedSettings);
        } catch (error) {
          logger.out.error("Error parsing popup settings: " + error);
        }
      }
    }

    this.ccyPairsList.forEach((pair: CurrencyPairImpl) => {
      let temperatureSettingsObj: BfxCcypairTempSetting = new BfxCcypairTempSetting(
        pair.base + "/" + pair.nonBase,
        Object.assign(new BigNumber(0), pair.orderTempWarmValue),
        Object.assign(new BigNumber(0), pair.orderTempHotValue),
        pair.temperatureTypeId ?? CubiqTS.UnitType.PERCENT,
        true,
        Object.assign(new BigNumber(0), pair.orderTempWarmValue),
        Object.assign(new BigNumber(0), pair.orderTempHotValue),
        pair.temperatureTypeId,
      );
      let savedVal: any = savedSettingsValues.find((val) => {
        return val.ccyPairIdentifier == pair.base + "/" + pair.nonBase;
      });

      this.buildObjectFromPair(temperatureSettingsObj, pair);
      if (savedVal != undefined) {
        this.buildObjectFromSavedValue(temperatureSettingsObj, savedVal);
      }

      temperatureSettingsObj.remember();
      this.updateDefaultFlags(temperatureSettingsObj);

      this.ccyPairsObjList.push(temperatureSettingsObj);
    });

    this.totalRecords = this.ccyPairsObjList.length;
    this.ccyPairsObjList.sort(CCY_PAIR_SORT);
    this.ready = true;
    this.cd.detectChanges();
  }

  private updateDefaultFlags(settings: BfxCcypairTempSetting): void {
    if (!settings.previousValues.warmSetting || !settings.previousValues.hotSetting) {
      settings.isEdited = false;
      settings.isDefault = true;
    } else if (!settings.warmSetting || !settings.hotSetting) {
      settings.isEdited = true;
      settings.isDefault = false;
    } else {
      settings.isDefault =
        settings.warmSetting.isEqualTo(settings.defaultWarm) &&
        settings.hotSetting.isEqualTo(settings.defaultHot) &&
        settings.temperatureTypeId == settings.defaultTypeId;
      settings.isEdited =
        !settings.isDefault ||
        !settings.warmSetting.isEqualTo(settings.previousValues.warmSetting) ||
        !settings.hotSetting.isEqualTo(settings.previousValues.hotSetting) ||
        settings.temperatureTypeId != settings.previousValues.temperatureTypeId;
    }
  }

  loadPairsLazy(event: LazyLoadEvent) {
    if (this.filterValue.length > 0) {
      this.ccyPairObjRecords = this.ccyPairsObjList
        .filter((a) =>
          a.ccyPairIdentifier
            .toLowerCase()
            .replace("/", "")
            .includes(this.filterValue.toLowerCase().replace("/", "")),
        )
        .slice(event.first, event.first + event.rows);
    } else
      this.ccyPairObjRecords = this.ccyPairsObjList.slice(event.first, event.first + event.rows);
    this.loading = false;

    this.cd.detectChanges();
  }

  private buildObjectFromPair(temperatureSettingsObj: BfxCcypairTempSetting, value: any): void {
    temperatureSettingsObj.typeSetting = this.temperatureTypes.get(
      value?.temperatureTypeId ?? UnitType.PERCENT,
    );
    temperatureSettingsObj.prevType = temperatureSettingsObj.typeSetting;
    temperatureSettingsObj.precision =
      value.temperatureTypeId == UnitType.PIPS ? this.pipsPrecision : this.percentagePrecision;
    temperatureSettingsObj.minValue =
      value.temperatureTypeId == UnitType.PIPS ? this.pipsMinVal : this.percentageMinVal;
    temperatureSettingsObj.maxValue =
      value.temperatureTypeId == UnitType.PIPS ? this.pipsMaxVal : this.percentageMaxVal;
  }

  private buildObjectFromSavedValue(
    temperatureSettingsObj: BfxCcypairTempSetting,
    savedVal: any,
  ): void {
    temperatureSettingsObj.temperatureTypeId = savedVal.temperatureTypeId;
    temperatureSettingsObj.typeSetting = this.temperatureTypes.get(
      temperatureSettingsObj.temperatureTypeId,
    );
    temperatureSettingsObj.prevType = temperatureSettingsObj.typeSetting;
    if (savedVal.warmSetting) {
      temperatureSettingsObj.warmSetting = new BigNumber(savedVal.warmSetting);
    }
    if (savedVal.hotSetting) {
      temperatureSettingsObj.hotSetting = new BigNumber(savedVal.hotSetting);
    }
    temperatureSettingsObj.precision =
      temperatureSettingsObj.temperatureTypeId == UnitType.PIPS
        ? this.pipsPrecision
        : this.percentagePrecision;
    temperatureSettingsObj.minValue =
      temperatureSettingsObj.temperatureTypeId == UnitType.PIPS
        ? this.pipsMinVal
        : this.percentageMinVal;
    temperatureSettingsObj.maxValue =
      temperatureSettingsObj.temperatureTypeId == UnitType.PIPS
        ? this.pipsMaxVal
        : this.percentageMaxVal;
  }

  ngOnDestroy() {}

  public validate(): Promise<BfxUserSettingsValidityState> {
    return new Promise<BfxUserSettingsValidityState>((res, reject) => {
      for (let i = 0; i < this.ccyPairsObjList.length; i++) {
        let value = this.ccyPairsObjList[i];

        if (this.getUnitTypeIdFromTemperature(value.typeSetting.value) == null) {
          this.failValidation = true;
          res(BfxUserSettingsValidityState.INVALID);
          return;
        }
      }

      res(BfxUserSettingsValidityState.VALID);
    });
  }

  public reset(): Promise<void> {
    return new Promise<void>((res, reject) => {
      let activeUser: ActiveUser = this.parentComponent.activeUser;
      let bookId = activeUser.defaultPositionBookId;
      let bookname = activeUser.defaultPositionBookName;
      for (let i = 0; i < this.ccyPairsObjList.length; i++) {
        if (this.ccyPairsObjList[i].isEdited) {
          this.ccyPairsObjList[i] = this.ccyPairsObjList[i].reset();
          this.updateDefaultFlags(this.ccyPairsObjList[i]);
        }
      }
      this.cd.detectChanges();
      res();
    });
  }

  public save(request: ProfileUpdateRequestImpl): void {
    let savedTempSetting: CubiqTS.ProfileSettingImpl = request.settings.find((setting) => {
      return (
        setting.profileSettingKey == ProfileSettingKeys.userProfileSettingsOrderBlotterTemperatures
      );
    });
    let valuesToSave: Array<any> = [];
    let settingObject: CubiqTS.ProfileSettingImpl = new CubiqTS.ProfileSettingImpl();
    settingObject.profileSettingKey =
      ProfileSettingKeys.userProfileSettingsOrderBlotterTemperatures;
    this.ccyPairsObjList.forEach((value) => {
      if (!value.isDefault) {
        let objToSave: any = {
          ccyPairIdentifier: value.ccyPairIdentifier,
          warmSetting: value.warmSetting,
          hotSetting: value.hotSetting,
          temperatureTypeId: value.temperatureTypeId,
          defaultWarm: value.defaultWarm,
          defaultHot: value.defaultHot,
          defaultTypeId: value.defaultTypeId,
        };
        valuesToSave.push(objToSave);
      } else if (value.isEdited) {
        // We can Edit the setting to make them default - need to update the prev vals in this case so
        // pair does not highlight as changed
        value.remember();
      }
    });
    this.tempValueToUpdateActiveUser = JSON.stringify(valuesToSave);
    if (savedTempSetting === undefined) {
      settingObject.profileSettingValue = this.tempValueToUpdateActiveUser;
      request.settings.push(settingObject);
    } else savedTempSetting.profileSettingValue = this.tempValueToUpdateActiveUser;
  }

  public postReset(): void {
    if (this.first == 0) {
      this.ccyPairsObjList = this.ccyPairsObjList.sort(CCY_PAIR_SORT);
      this.ccyPairObjRecords = this.ccyPairsObjList.slice(0, this.PAIR_TEMPERATURE_ROW_COUNT);
    } else {
      this.ccyPairsObjList.sort(CCY_PAIR_SORT);
      setTimeout(() => {
        this.first = 0;
      }, 10);
    }
  }

  public postSave(request: ProfileUpdateRequestImpl): void {
    let activeUser: ActiveUser = this.parentComponent.activeUser;
    if (activeUser.profileSettings) {
      activeUser.profileSettings.set(
        ProfileSettingKeys.userProfileSettingsOrderBlotterTemperatures,
        this.tempValueToUpdateActiveUser,
      );
    }
    this.ccyPairsObjList.forEach((val) => this.updateDefaultFlags(val));
    if (this.first == 0) {
      this.ccyPairsObjList = this.ccyPairsObjList.sort(CCY_PAIR_SORT);
      this.ccyPairObjRecords = this.ccyPairsObjList.slice(0, this.PAIR_TEMPERATURE_ROW_COUNT);
    } else {
      this.ccyPairsObjList.sort(CCY_PAIR_SORT);
      setTimeout(() => {
        this.first = 0;
      }, 10);
    }
  }

  public onComponentStateChanged(componentState: BfxUserSettingsComponentState): void {
    this.panelHeight = componentState.componentBounds.height - 120;
  }

  public searchType(event) {
    let query: string = event.query.toLowerCase();
    this.typeSuggestions = [...this.temperatureTypes.values()].filter(
      (a) => a.label.toLowerCase().indexOf(query) >= 0,
    );

    // if input is invalid, show all suggestions, dovetails with autoHighlight
    if (this.typeSuggestions.length == 0) {
      this.typeSuggestions = [...this.temperatureTypes.values()];
    }

    this.cd.markForCheck();
  }

  public rowEditedType(event, data: BfxCcypairTempSetting): void {
    if (event.label && event.label === data.prevType?.label) {
      if (this.failValidation) {
        this.parentComponent.validate(this.categoryComponent);
        this.failValidation = false;
      }
      return;
    } else if (typeof event === "string" && data.prevType.label === event) {
      // revert on matching
      data.typeSetting = data.prevType;
    }

    this.updateOnTemperatureTypeChange(data);
    this.rowEdited(event, data);
  }

  public rowEditedTemp(event, data: BfxCcypairTempSetting): void {
    if (!data.hotSetting || !data.warmSetting) {
      // Without setTimeout - setting the input temp to a non-null value is sometimes not propagated back to spinner if e.g. you
      // tap the dell key twice in the input
      setTimeout(() => {
        if (!data.hotSetting) {
          data.hotSetting = this.scaleTemperature(data.defaultHot, data);
        }
        if (!data.warmSetting) {
          data.warmSetting = this.scaleTemperature(data.defaultWarm, data);
        }
        this.rowEdited(event, data);
      }, 0);
    } else {
      this.rowEdited(event, data);
    }
  }

  private scaleTemperature(value: BigNumber, data: BfxCcypairTempSetting): BigNumber {
    let retValue = value;
    if (data.temperatureTypeId != data.defaultTypeId) {
      const scale =
        data.temperatureTypeId == UnitType.PIPS ? new BigNumber("100") : new BigNumber("0.01");
      retValue = value.times(scale);
    }
    return retValue;
  }

  private rowEdited(event, data: BfxCcypairTempSetting) {
    if (data.temperatureTypeId == UnitType.PIPS) {
      data.minValue = this.pipsMinVal;
      data.maxValue = this.pipsMaxVal;
    } else {
      data.minValue = this.percentageMinVal;
      data.maxValue = this.percentageMaxVal;
    }

    if (
      data.warmSetting &&
      ((data.temperatureTypeId == UnitType.PIPS && data.warmSetting.isLessThan(1)) ||
        (data.temperatureTypeId == UnitType.PERCENT && data.warmSetting.isLessThan(0.01)))
    ) {
      data.warmSetting =
        data.temperatureTypeId == UnitType.PIPS ? new BigNumber(1) : new BigNumber(0.01);
    }

    if (
      data.warmSetting &&
      data.hotSetting &&
      (data.warmSetting.isEqualTo(data.hotSetting) || data.warmSetting.isLessThan(data.hotSetting))
    ) {
      let newWarmValue =
        data.temperatureTypeId == UnitType.PIPS
          ? data.warmSetting.minus(1)
          : data.warmSetting.minus(0.01);
      data.hotSetting = new BigNumber(newWarmValue);
    }

    this.updateDefaultFlags(data);
    this.parentComponent.validate(this.categoryComponent);
  }

  private getUnitTypeIdFromTemperature(value): CubiqTS.UnitType {
    return [...this.temperatureTypes.entries()].find(([_, v]) => value === v?.value)?.[0];
  }

  private updateOnTemperatureTypeChange(data: BfxCcypairTempSetting): void {
    const typeId = this.getUnitTypeIdFromTemperature(data?.typeSetting?.value);
    if (typeId == null) {
      logger.out.warn("Cannot find temperature unit");
      return;
    }
    if (typeId != data.temperatureTypeId) {
      data.temperatureTypeId = typeId;
      data.hotSetting =
        data.temperatureTypeId == UnitType.PIPS
          ? data.hotSetting.times(100)
          : data.hotSetting.dividedBy(100);
      data.warmSetting =
        data.temperatureTypeId == UnitType.PIPS
          ? data.warmSetting.times(100)
          : data.warmSetting.dividedBy(100);
      data.precision = data.temperatureTypeId == UnitType.PIPS ? 0 : 2;
      data.prevType = data.typeSetting;
    }
  }

  public toggleSearch() {
    this.showSearch = !this.showSearch;
    if (!this.showSearch) {
      this.onChangeSearchValue("");
    }
  }

  public revertToDefault(event, data: BfxCcypairTempSetting): void {
    let ccyPair: any = this.ccyPairsList.find((pair) => {
      return pair.base + "/" + pair.nonBase == data.ccyPairIdentifier;
    });
    if (ccyPair != undefined) {
      data.remember();

      data.warmSetting = Object.assign(new BigNumber(0), ccyPair.orderTempWarmValue);
      data.hotSetting = Object.assign(new BigNumber(0), ccyPair.orderTempHotValue);
      data.temperatureTypeId = ccyPair.temperatureTypeId;

      this.buildObjectFromPair(data, ccyPair);

      this.updateDefaultFlags(data);
      this.parentComponent.validate(this.categoryComponent);
    }
  }

  public onChangeSearchValue(event) {
    this.filterValue = event;
    this.search.next();
  }

  private subscribeToSearch(): void {
    this.search = new EventEmitter<string>();
    this.search.pipe(debounceTime(500)).subscribe(() => {
      this.ccyPairObjRecords = this.ccyPairsObjList.filter((a) =>
        a.ccyPairIdentifier
          .toLowerCase()
          .replace("/", "")
          .includes(this.filterValue.toLowerCase().replace("/", "")),
      );
      this.totalRecords = this.ccyPairObjRecords.length;
      this.first = 0;
      this.cd.detectChanges();
    });
  }

  public hasDataChanged(): boolean {
    return null;
  }
}
